import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import type { Scene } from '@babylonjs/core/scene';
import { supportCardName } from '@archon3d/core';
import {
  STORY_PACE,
  chanceNow,
  storyHeadline,
  storyMoment,
  storyNarration,
  sweep,
} from '../../game/cardStory';
import type { CardClashView, GameSession, SessionSnapshot } from '../../game/session';
import { Tablet, type TabletItem } from '../tablets/tablet';
import { BOARD_CENTER, TABLE_HEIGHT } from '../board/constants';

/**
 * The lines under a fighter's portrait: its side, by the board's name for it, and level; then its
 * roster rating, or after the reveal its chance in this fight and the outcome, with a note saying
 * which of the two it is. The note used to call a revealed chance a roster rating.
 */
export function fighterCaption(
  snapshot: Pick<SessionSnapshot, 'ctx'>,
  clash: CardClashView,
  fighter: CardClashView['participants'][number],
): { side: string; figure: string; note: string } {
  const score = clash.scores?.find((s) => s.pieceId === fighter.pieceId);
  const side =
    snapshot.ctx.board.def.factions.find((f) => f.id === fighter.faction)?.name ?? fighter.faction;
  const level =
    score?.level ?? fighter.level ?? snapshot.ctx.cardLevels?.[fighter.faction] ?? 'expert';
  // While the clash is told, the chance as it stands; the outcome once it is over.
  const chance = chanceNow(clash.story, fighter.pieceId) ?? score?.probability;
  const outcome = score?.won !== undefined && storyMoment(clash.story).over;
  return chance !== undefined
    ? {
        side: `${side} · ${level}`,
        figure: `${Math.round(chance * 100)}%${outcome ? ` · ${score!.won ? 'Winner' : 'Defeated'}` : ' chance'}`,
        note: 'This fight’s chance',
      }
    : {
        side: `${side} · ${level}`,
        figure: `Expert rating ${fighter.main}`,
        note: 'Roster rating; not this fight’s chance',
      };
}

/** Shared desktop/XR scene geometry; only the local chooser's hand is ever materialised. */
export function createCardStage(
  scene: Scene,
  parent: TransformNode,
  board: TransformNode,
  session: GameSession,
) {
  const root = new TransformNode('cardCombatStage', scene);
  root.parent = parent;
  const cells: Mesh[] = [];
  for (let r = -2; r <= 2; r++)
    for (let q = Math.max(-2, -r - 2); q <= Math.min(2, -r + 2); q++) {
      const hex = MeshBuilder.CreateCylinder(
        'greenCardHex',
        { diameter: 0.205, height: 0.012, tessellation: 6 },
        scene,
      );
      hex.position.set((q + r / 2) * 0.18, TABLE_HEIGHT + 0.04, BOARD_CENTER.z + r * 0.156);
      hex.rotation.y = Math.PI / 6;
      cells.push(hex);
    }
  const mat = new StandardMaterial('cardBoardGreen', scene);
  mat.diffuseColor = Color3.FromHexString('#397a3f');
  const grid = Mesh.MergeMeshes(cells, true, true)!;
  grid.name = 'cardHexBoard';
  grid.parent = root;
  grid.material = mat;
  grid.isPickable = false;
  const illustrations = new Map<string, HTMLImageElement>();
  let cards: Tablet[] = [];
  let disposed = false;
  let time = 0;
  let loading = false;
  let lastView: unknown;
  const make = (
    name: string,
    x: number,
    z: number,
    y: number,
    width: number,
    height: number,
    items: TabletItem[],
  ) => {
    const card = new Tablet(scene, {
      name,
      width,
      height,
      texWidth: 400,
      texHeight: 620,
      position: new Vector3(x, y, z),
      tilt: Math.PI / 3,
      parent: root,
    });
    card.setItems(items);
    cards.push(card);
    return card;
  };
  const art = (character: string, name: string): TabletItem[] => {
    const image = illustrations.get(character);
    return image
      ? [{ kind: 'image', image, rect: { x: 30, y: 30, w: 340, h: 300 } }]
      : [{ kind: 'text', text: name, x: 200, y: 190, size: 35, align: 'center', maxWidth: 340 }];
  };
  /** The tablets of the fighters and of their played supports, and the chances between them. */
  const mains = new Map<number, Tablet>();
  const holders = new Map<number, Tablet>();
  let chances: Tablet | null = null;
  /** When the told clash began to roll, on the stage's clock, and the needle last drawn then. */
  let rollingSince: number | null = null;
  let needleDrawn = -1;

  const mainItems = (
    snapshot: SessionSnapshot,
    clash: CardClashView,
    p: CardClashView['participants'][number],
  ): TabletItem[] => {
    const score = clash.scores?.find((s) => s.pieceId === p.pieceId);
    const caption = fighterCaption(snapshot, clash, p);
    return [
      ...art(p.character, p.name),
      { kind: 'text', text: p.name, x: 200, y: 365, size: 38, align: 'center', maxWidth: 340 },
      {
        kind: 'text',
        text: caption.side,
        x: 200,
        y: 410,
        size: 25,
        align: 'center',
        maxWidth: 340,
      },
      {
        kind: 'text',
        text: caption.figure,
        x: 200,
        y: 464,
        size: 36,
        align: 'center',
        maxWidth: 340,
      },
      {
        kind: 'text',
        text: caption.note,
        x: 200,
        y: 566,
        size: 18,
        align: 'center',
        maxWidth: 360,
      },
      {
        kind: 'text',
        text:
          score?.probability !== undefined
            ? `Expert ${p.main} · ${score.source === 'measured' ? 'Arena baseline' : 'Estimate'}`
            : `${snapshot.ctx.roster[p.character]?.hitPoints.toFixed(1)} HP · ${snapshot.ctx.roster[p.character]?.attack.damage.toFixed(1)} damage`,
        x: 200,
        y: 524,
        size: 24,
        align: 'center',
        maxWidth: 340,
      },
    ];
  };

  /** A played support: face down until the telling lays it down, then the card itself. */
  const holderItems = (
    snapshot: SessionSnapshot,
    clash: CardClashView,
    p: CardClashView['participants'][number],
  ): TabletItem[] => {
    const score = clash.scores?.find((s) => s.pieceId === p.pieceId);
    // While a person chooses: the AI still over its card says so; a side that chose is face down.
    if (clash.phase === 'choose') {
      const choosing = clash.choosing?.includes(p.faction);
      return [
        { kind: 'text', text: choosing ? '…' : '?', x: 200, y: 250, size: 150, align: 'center' },
        {
          kind: 'text',
          text: choosing ? `${p.name} is choosing` : 'Chosen',
          x: 200,
          y: 390,
          size: 30,
          align: 'center',
          maxWidth: 340,
        },
      ];
    }
    const order = clash.story?.order ?? [];
    const down =
      clash.phase === 'reveal' && order.indexOf(p.pieceId) < storyMoment(clash.story).placed;
    if (clash.phase === 'reveal' && !down)
      return [
        { kind: 'text', text: '?', x: 200, y: 250, size: 150, align: 'center' },
        { kind: 'text', text: 'Support', x: 200, y: 390, size: 34, align: 'center', maxWidth: 340 },
      ];
    const support = score?.cardId;
    const character = support?.startsWith('character:')
      ? support.slice(10)
      : support?.startsWith('element:')
        ? `elemental-${support.slice(8)}`
        : null;
    const name = support
      ? supportCardName(snapshot.ctx, support)
      : score
        ? 'No support'
        : 'Hidden support';
    return [
      ...(character ? art(character, name) : []),
      { kind: 'text', text: name, x: 200, y: 390, size: 34, align: 'center', maxWidth: 340 },
      ...(score?.cardId
        ? [
            {
              kind: 'text' as const,
              text: `${score.role}${score.fit ? ' · fit' : ''}${score.counter ? ' · counter' : ''} · +${Math.round(score.support)}`,
              x: 200,
              y: 440,
              size: 26,
              align: 'center' as const,
              maxWidth: 360,
            },
          ]
        : []),
    ];
  };

  /** The chances bar between the fighters: what the telling says, the bar, and the roll's needle. */
  const chanceItems = (
    snapshot: SessionSnapshot,
    clash: CardClashView,
    needle: number | undefined,
  ): TabletItem[] => {
    const story = clash.story;
    const scores = clash.scores ?? [];
    const moment = storyMoment(story);
    const order = story?.order ?? clash.participants.map((p) => p.pieceId);
    const headline = storyHeadline(clash.participants, scores);
    const factions = snapshot.ctx.board.def.factions;
    const segments = order.map((id) => {
      const p = clash.participants.find((x) => x.pieceId === id);
      return {
        label: p?.name ?? '',
        color: factions.find((f) => f.id === p?.faction)?.color ?? '#777777',
        share: chanceNow(story, id) ?? scores.find((s) => s.pieceId === id)?.probability ?? 0,
      };
    });
    return [
      {
        kind: 'text',
        text: moment.over
          ? headline.title
          : storyNarration(snapshot.ctx, clash.participants, scores, story),
        x: 400,
        y: 70,
        size: moment.over ? 52 : 38,
        align: 'center',
        maxWidth: 760,
      },
      ...(moment.over && headline.note
        ? [
            {
              kind: 'text' as const,
              text: headline.note,
              x: 400,
              y: 116,
              size: 28,
              align: 'center' as const,
              maxWidth: 760,
            },
          ]
        : []),
      {
        kind: 'balance',
        rect: { x: 30, y: 150, w: 740, h: 80 },
        segments,
        fontSize: 30,
        ...(needle !== undefined ? { needle } : {}),
      },
    ];
  };

  /** A told clash moved a step: only the tablets the step changes are drawn again. */
  const tellInPlace = (snapshot: SessionSnapshot, clash: CardClashView) => {
    for (const p of clash.participants) {
      mains.get(p.pieceId)?.setItems(mainItems(snapshot, clash, p));
      holders.get(p.pieceId)?.setItems(holderItems(snapshot, clash, p));
    }
    const moment = storyMoment(clash.story);
    if (moment.rolling && rollingSince === null) rollingSince = time;
    if (!moment.rolling) rollingSince = null;
    chances?.setItems(chanceItems(snapshot, clash, moment.over ? clash.story?.draw : undefined));
  };

  const rebuild = () => {
    if (disposed) return;
    const snapshot = session.getSnapshot(),
      clash = snapshot.cardClash;
    const active = !!clash;
    root.setEnabled(active);
    board.setEnabled(!active);
    if (clash === lastView) return;
    const told =
      clash?.phase === 'reveal' &&
      (lastView as CardClashView | null | undefined)?.phase === 'reveal' &&
      (lastView as CardClashView).scores === clash.scores;
    const previous = lastView as CardClashView | null | undefined;
    // Another side chose while this one's hand is up: its holder changes, the hand stays.
    const choosing =
      clash?.phase === 'choose' &&
      previous?.phase === 'choose' &&
      previous.chooser === clash.chooser &&
      previous.hand === clash.hand;
    lastView = clash;
    if (told) {
      tellInPlace(snapshot, clash);
      return;
    }
    if (choosing) {
      for (const p of clash.participants)
        holders.get(p.pieceId)?.setItems(holderItems(snapshot, clash, p));
      return;
    }
    cards.forEach((c) => c.dispose());
    cards = [];
    mains.clear();
    holders.clear();
    chances = null;
    rollingSince = null;
    if (!clash) return;
    const chooser =
      clash.chooser ??
      clash.participants.find((p) => snapshot.seats[p.faction] === 'human')?.faction;
    const participants = [...clash.participants].sort(
      (a, b) => +(b.faction === chooser) - +(a.faction === chooser),
    );
    participants.forEach((p, i) => {
      const angle = Math.PI / 2 + (i * 2 * Math.PI) / participants.length;
      mains.set(
        p.pieceId,
        make(
          `main-${p.pieceId}`,
          Math.cos(angle) * 0.43,
          BOARD_CENTER.z + Math.sin(angle) * 0.33,
          TABLE_HEIGHT + 0.1,
          0.17,
          0.25,
          mainItems(snapshot, clash, p),
        ),
      );
      if (p.faction !== clash.chooser || clash.phase === 'reveal')
        holders.set(
          p.pieceId,
          make(
            `opponent-holder-${p.pieceId}`,
            Math.cos(angle) * 0.66,
            BOARD_CENTER.z + Math.sin(angle) * 0.52,
            TABLE_HEIGHT + 0.09,
            0.1,
            0.15,
            holderItems(snapshot, clash, p),
          ),
        );
    });
    if (clash.phase === 'reveal') {
      const card = new Tablet(scene, {
        name: 'cardChances',
        width: 0.4,
        height: 0.13,
        texWidth: 800,
        texHeight: 260,
        position: new Vector3(0, TABLE_HEIGHT + 0.1, BOARD_CENTER.z),
        tilt: Math.PI / 3,
        parent: root,
      });
      cards.push(card);
      chances = card;
      tellInPlace(snapshot, clash);
    }
    if (clash.phase === 'choose' && clash.chooser) {
      clash.hand.forEach((c, i) => {
        const character = c.id.startsWith('character:')
          ? c.id.slice(10)
          : `elemental-${c.id.slice(8)}`;
        const items: TabletItem[] = [
          ...art(character, c.name),
          { kind: 'text', text: c.name, x: 200, y: 362, size: 31, align: 'center', maxWidth: 340 },
          { kind: 'text', text: c.fit, x: 200, y: 386, size: 18, align: 'center', maxWidth: 360 },
        ];
        const roles = c.water ? (['pressure', 'protection', 'deception'] as const) : [c.role];
        roles.forEach((role, j) =>
          items.push({
            kind: 'gem',
            id: `${c.id}-${role}`,
            rect: { x: 25, y: 395 + j * 61, w: 350, h: 55 },
            gem: 'emerald',
            label: role,
            fontSize: 27,
            enabled: true,
            onTap: () => session.commitCardSupport(c.id, role),
          }),
        );
        make(
          `support-${i}`,
          (i - (clash.hand.length - 1) / 2) * 0.165,
          BOARD_CENTER.z + 0.68,
          TABLE_HEIGHT + 0.16,
          0.15,
          0.23,
          items,
        );
      });
      make('cardPass', 0, BOARD_CENTER.z + 0.88, TABLE_HEIGHT + 0.09, 0.18, 0.055, [
        {
          kind: 'gem',
          id: 'pass',
          rect: { x: 20, y: 180, w: 360, h: 260 },
          gem: 'diamond',
          label: 'No support',
          fontSize: 37,
          enabled: true,
          onTap: () => session.commitCardSupport(null),
        },
      ]);
    }
    if (!loading) {
      loading = true;
      // The cards are built again once, when every portrait is in. They used to be rebuilt as each
      // portrait arrived: every card and its face texture, a dozen and more times over while the
      // player was choosing.
      const arrivals: Promise<void>[] = [];
      void import('../../editor/portraitRenderer')
        .then(({ renderPortraits }) =>
          renderPortraits(
            snapshot.ctx.roster,
            () => disposed,
            (id, url) => {
              if (disposed) return;
              arrivals.push(
                new Promise<void>((resolve) => {
                  const image = new Image();
                  image.onload = () => {
                    if (!disposed) illustrations.set(id, image);
                    resolve();
                  };
                  image.onerror = () => resolve();
                  image.src = url;
                }),
              );
            },
          ),
        )
        .then(() => Promise.all(arrivals))
        .then(() => {
          if (disposed || illustrations.size === 0) return;
          lastView = null;
          rebuild();
        })
        .catch(() => {
          /* Character names remain visible if an optional asset fails. */
        });
    }
  };
  const unsub = session.subscribe(rebuild);
  rebuild();
  const pointer = scene.onPointerObservable.add((info) => {
    if (info.type !== PointerEventTypes.POINTERTAP || !root.isEnabled() || !info.pickInfo) return;
    cards.some((c) => c.tap(info.pickInfo!, time));
  });
  const frame = scene.onBeforeRenderObservable.add(() => {
    time += scene.getEngine().getDeltaTime() / 1000;
    // The roll's needle, drawn a score of times a second rather than every frame of the headset.
    const clash = lastView as CardClashView | null;
    if (rollingSince !== null && chances && clash?.story && time - needleDrawn >= 0.05) {
      needleDrawn = time;
      const u = Math.min(1, (time - rollingSince) / (STORY_PACE.roll / 1000));
      chances.setItems(chanceItems(session.getSnapshot(), clash, sweep(u, clash.story.draw)));
    }
    cards.forEach((c) => c.update(time, 6));
  });
  return {
    dispose() {
      disposed = true;
      unsub();
      scene.onPointerObservable.remove(pointer);
      scene.onBeforeRenderObservable.remove(frame);
      cards.forEach((c) => c.dispose());
      root.dispose();
      mat.dispose();
      board.setEnabled(true);
    },
  };
}
